<template>
  <main>
    <section class="error" v-if="error">
      {{ error }}
    </section>

    <div class="action-bar sb-row">
      <section>
        <div class="title">impact report inquiry</div>
      </section>
    </div>

    <section class="no-reports">
      <p>
        select an event and a kpi, or select from one of our popular inquiries and we’ll show you which <br/>
        selection (brand, reward, day, etc.) has the biggest impact on the selected kpi
      </p>
    </section>

    <section
      v-if="showEventsList"
      class="inquiry-types flex flex-col gap-3 mx-4 md:mx-auto md:w-9/12 max-w-2xl"
    >
      <button v-for="eventType in eventTypesShortcuts"
         @click="presetValuesToGenerateReport(eventType)"
         :key="eventType.key"
         class="sb-button text-center text-lg p-6"
      >
        see which <span class="event-type-label">{{eventType.short_label}}</span>
        has the biggest impact on a kpi
      </button>
    </section>
    <section v-else>
      <div class="dropdown-row">
        <div class="impact-row">
          <section class="content flex flex-1">
            <sb-async-dropdown
              :rows="eventDropdown.rows"
              :selected="eventDropdown.selected"
              :focused.sync="eventDropdown.focused"
              :options="eventDropdown.options"
              @update:selected="updateSelectedEvent"
              class="events-dropdown flex-1"
            />
          </section>

          <section class="content">
            <sb-async-dropdown
              :rows="kpisDropdown.rows"
              :selected="kpisDropdown.selected"
              :focused.sync="kpisDropdown.focused"
              :options="kpisDropdown.options"
              @update:selected="updateSelectedKpi"
              class="events-dropdown kpi-selection"
            />
          </section>
        </div>

        <div>
          <section class="action-bar sb-row" style="justify-content: flex-end">
            <div>
              <button
                class="button run-inquiry-button"
                @click="initiateReportJob"
                :disabled="disableRunReport || isLoadingReport"
              >
                see impact
                <sb-fai class="text-md ml-1" :icon="['fas', 'chevron-right']" />
              </button>
            </div>
          </section>
        </div>
      </div>

      <section v-if="isLoadingReport" class="loading-container">
        <h3>inquiry report in progress</h3>
        <p>processing...</p>
        <sb-loading-icon />
      </section>
      <div>
        <sb-2d-table
          class="impact-report"
          :columns="columns"
          :rows="rows"
          :cells="cells"
        >
          <template #column="{ column }">
            {{ generateLabel(column.label) }}
          </template>
          <template #cell="{ row, column }">
            <span class="cell" :class="{ 'with-secondary': cells[row.key][column.key].secondary }">
              <div class="primary">{{cells[row.key][column.key].primary}}</div>
              <div class="secondary">{{cells[row.key][column.key].secondary}}</div>
            </span>
          </template>
        </sb-2d-table>
      </div>
      <div v-if="noRecordsFoundMessage" class="loading-container title">
        no results found
      </div>
    </section>

    <popular-inquiries-drawer
      :drawerExpanded.sync="drawerExpanded"
      :eventTypes="eventTypes"
      :kpis="kpis"
      :onPresetClick="onPresetClick"
      :isLoadingReport="isLoadingReport"
    />
  </main>
</template>

<script>
import { reportsAPI } from "../../../services/api/reportsAPI";
import { mapState } from "vuex";
import PopularInquiriesDrawer from "./PopularInquiriesDrawer";

export default {
  name: 'InquiryReport',

  components: {
    PopularInquiriesDrawer
  },

  data() {
    return {
      drawerExpanded: true,

      columns: [],
      rows: [],
      cells: {},
      error: null,
      showEventsList: true,
      disableRunReport: true,
      reportId: null,
      isLoadingReport: false,
      noRecordsFoundMessage: false,

      eventTypes: [],
      kpis: [],

      eventDropdown: {
        rows: [],
        selected: null,
        focused: false,
        options: {
          label: (selected) => `see which ${selected.short_label?.toLowerCase()} has the biggest impact on a kpi`
        }
      },
      kpisDropdown: {
        rows: [],
        selected: null,
        focused: false,
        options: {
          label: (selected) => selected ? selected.label?.toLowerCase() : 'select a kpi'
        }
      }
    }
  },

  async created() {
    this.$store.dispatch('LOAD_CURRENT_MERCHANT')

    try {
      await this.fetchEventTypes();
      await this.fetchKpis();
    } catch(err) {
      this.handleError(err);
    }
  },

  computed: {
    eventTypesShortcuts() {

      if(!this.eventDropdown.rows.length) return []

      const eventTypes = this.eventDropdown.rows.reduce( (eventTypes, row) => (
        {...eventTypes, [row.state.key]: row.state}
      ), {} )

      return [
        eventTypes['category_purchased'],
        eventTypes['brand_purchased'],
        eventTypes['purchased_on_day_of_week'],
        eventTypes['reward_redeemed'],
        eventTypes['offer_redeemed'],
        eventTypes['visit_count_achieved'],
        eventTypes['redemption_count_achieved'],
      ];
    },
    ...mapState(['currentMerchant']),
  },

  methods: {
    onPresetClick(preset) {
      const event = this.eventTypes.find(e => e.key === preset.event);
      const kpi = this.kpis.find(k => k.key === preset.kpi);
      this.presetValuesToGenerateReport(event, kpi)
      this.initiateReportJob();
    },
    async fetchEventTypes() {
      this.eventTypes = await reportsAPI.fetchInquiryReportEventTypes()
      this.eventDropdown.rows = this.eventTypes.map((row) => {
        return {
          slot: 'select-row',
          state: row
        }
      })
    },
    async fetchKpis() {
      this.kpis = await reportsAPI.fetchInquiryReportKpis()
      this.kpisDropdown.rows = this.kpis.map((row) => {
        return {
          slot: 'select-row',
          state: row
        }
      });
    },
    presetValuesToGenerateReport(eventType, kpi=null) {
      this.showEventsList = false;
      this.updateSelectedEvent(eventType)
      if(kpi) this.updateSelectedKpi(kpi)
    },
    updateSelectedEvent(selectedEvent) {
      this.eventDropdown.selected = { key: selectedEvent.key, short_label: selectedEvent.short_label };
      this.noRecordsFoundMessage = false;
    },
    updateSelectedKpi(selectedKpi) {
      this.kpisDropdown.selected = { key: selectedKpi.key, label: selectedKpi.label };
      this.disableRunReport = false;
    },
    async initiateReportJob() {
      this.isLoadingReport = true;
      this.noRecordsFoundMessage = false;
      this.tableReset();

      const options = {
        report: {
          type: 'Reports::ImpactInquiryReport',
          input_data: {
            inquiry_type: this.eventDropdown.selected.key,
            inquiry_kpi: this.kpisDropdown.selected.key,
          }
        }
      };

      const response = await reportsAPI.initializeReportJob(options)

      this.reportId = response.id
      this.pollReport();
    },
    pollReport() {
      setTimeout(() => {
        reportsAPI.fetchReport(this.reportId)
          .then((data) => {
            if (data.report.state === 'completed') {
              this.isLoadingReport = false;
              if(data.report.output_data.rows) {
                this.setTableData(data.report.output_data);
              } else {
                this.noRecordsFoundMessage = true;
              }
            } else {
              this.pollReport();
            }
          }).catch(this.handleError);
      }, 1000);
    },
    setTableData(data) {
      const rowsAndCells = data.rows.reduce((res, row) => {
        const key = row.label?.toLowerCase().replace(' ', '_');
        res.rows.push({
          label: row.label,
          key
        });

        res.cells[key] = row.values;

        return res;
      }, {
        rows: [],
        cells: {}
      });

      this.columns = data.cols;
      this.rows = rowsAndCells.rows;
      this.cells = rowsAndCells.cells;
    },
    generateLabel(label) {
      return label?.toLowerCase() || 'n/a'
    },
    handleError(e) {
      this.error = e;
      this.isLoadingReport = false;
    },
    tableReset() {
      this.columns = [];
      this.rows = [];
      this.cells = {};
    }
  },
}
</script>

<style lang="scss" scoped>
.title {
  margin-bottom: 12px;
  font-size: 20px;
}

.inquiry-title {
  margin-bottom: 12px;
  font-size: 16px;
}

.event-type-label {
  font-family: "AileronLight",sans-serif;
}

.no-reports {
  text-align: center;
}

.button {
  font-family: "AileronThin",sans-serif;
  font-size: 18px;
}

.button.run-inquiry-button:focus {
  background-color: #128ff9;
}

.button.larger {
  padding: 2rem;
  width: 75%
}

.impact-row {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
}

.events-dropdown ::v-deep {
  width: fit-content;
  min-width: 300px;
  margin: 0 0 16px;
  white-space: nowrap;
  main.label-container {
    flex-direction: row;
    justify-content: center;
    > div {
      flex: 1;
      justify-content: space-between;
    }
  }
}

.kpi-selection {
  margin-left: 24px;
}

.events-dropdown::v-deep .dropdown-item {
  text-transform: lowercase;
}

.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.sb-loading-icon {
  width: 100%;
  max-width: 200px;
}

.impact-report {
  align-items: center;
}
.impact-report::v-deep .column-container {
  margin-left: 50px;
  margin-right: 50px;
}

.sb-2d-table {
  margin-top: 38px;
}

.sb-2d-table .cell {
  min-width: 150px;
}

.sb-2d-table .cell.with-secondary {
  padding: 4px 25px;
}

.sb-2d-table .cell.with-secondary .primary {
  line-height: 18px;
}

.sb-2d-table .cell.with-secondary .secondary {
  line-height: 14px;
  font-size: 0.9em;
}

.run-inquiry-button {
  padding: 13px 30px;
  margin-top: 16px;
}
</style>
